/**
 * Centralized contact information for RO Auto Market
 * Update this file to change contact details across the entire site
 */

export const CONTACT_INFO = {
  // WhatsApp number (without + sign for wa.me link)
  whatsappNumber: '40732963134',
  // Formatted phone for display
  whatsappDisplay: '+40 732 963 134',
  // WhatsApp link
  whatsappLink: 'https://wa.me/40732963134',
  // Email
  email: 'contact@roautomarket.ro',
  // Address
  address: 'Strada Alexandru Marghiloman, Buzău, România',
  addressShort: 'Buzău, România',
  // Google Maps / Google Business Profile link
  googleMapsLink: 'https://www.google.com/maps/place/RO+Auto+Market/@45.1497655,26.8345422,20.25z/data=!4m6!3m5!1s0x40b15f648e2d8401:0xe7871887cc378651!8m2!3d45.1497989!4d26.8348065!16s%2Fg%2F11yyhskh9j',
  // Google Maps embed URL for iframe
  googleMapsEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2838.5!2d26.8!3d45.15!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b3b9e9f4f4f4f4%3A0x0!2sStrada%20Alexandru%20Marghiloman%2C%20Buz%C4%83u!5e0!3m2!1sen!2sro!4v1234567890',
  // Google Business Profile public link (share.google resolves here)
  googleBusinessProfile: 'https://www.google.com/maps/place/RO+Auto+Market/@45.1497655,26.8345422,20.25z/data=!4m6!3m5!1s0x40b15f648e2d8401:0xe7871887cc378651!8m2!3d45.1497989!4d26.8348065!16s%2Fg%2F11yyhskh9j',
  // Social media
  facebook: 'https://www.facebook.com/share/194VhaWZXC/',
  x: 'https://x.com/ROAutoMarket',
  youtube: 'https://youtube.com/@vladimirtudor3618?si=bAFGn4wegj4Wet_N',
  instagram: 'https://www.instagram.com/tudor_digital_marketing?igsh=MTM4YWg3NzhuMXhl',
  tiktok: 'https://www.tiktok.com/@www.roautomarket.ro',
} as const;

/**
 * Get WhatsApp link with optional pre-filled message
 */
export function getWhatsAppLink(message?: string): string {
  const baseUrl = CONTACT_INFO.whatsappLink;
  if (message) {
    return `${baseUrl}?text=${encodeURIComponent(message)}`;
  }
  return baseUrl;
}

/**
 * Lungimi valide (fără "+") pentru numerele naționale, pe țară.
 */
const COUNTRY_RULES: Record<string, { cc: string; nationalLengths: number[] }> = {
  RO: { cc: '40', nationalLengths: [9] },
  UK: { cc: '44', nationalLengths: [10] },
  DE: { cc: '49', nationalLengths: [10, 11] },
};

function rulesFor(country: string) {
  return COUNTRY_RULES[country] ?? COUNTRY_RULES.RO;
}

/**
 * Normalizează un număr de telefon pentru wa.me (fără "+", cu prefix de țară).
 * Implicit RO (40). Acceptă formate: 0732..., 0040732..., +40732..., 732...
 * Returnează null dacă numărul nu poate fi formatat corect.
 */
export function toWhatsAppNumber(
  raw: string | null | undefined,
  country: 'RO' | 'UK' | 'DE' | string = 'RO',
): string | null {
  if (!raw) return null;
  const { cc, nationalLengths } = rulesFor(country);
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = cc + digits.replace(/^0+/, '');
  // Număr național fără 0 inițial (ex. 732963134) -> adaugă prefixul
  else if (!digits.startsWith(cc) && digits.length <= Math.max(...nationalLengths)) {
    digits = cc + digits;
  }
  return isValidWhatsAppNumber(digits, country) ? digits : null;
}

/**
 * Verifică dacă un număr deja normalizat (doar cifre, cu prefix de țară)
 * este valid pentru wa.me.
 */
export function isValidWhatsAppNumber(
  digits: string | null | undefined,
  country: 'RO' | 'UK' | 'DE' | string = 'RO',
): boolean {
  if (!digits || !/^\d+$/.test(digits)) return false;
  const { cc, nationalLengths } = rulesFor(country);
  if (digits.startsWith(cc)) {
    const national = digits.slice(cc.length);
    if (national.startsWith('0')) return false;
    return nationalLengths.includes(national.length);
  }
  // Alt prefix internațional acceptat generic (8–15 cifre, standard E.164)
  return digits.length >= 8 && digits.length <= 15;
}

/**
 * Construiește linkul wa.me doar dacă numărul trece validarea.
 */
export function buildWhatsAppLink(
  raw: string | null | undefined,
  options: { country?: 'RO' | 'UK' | 'DE' | string; message?: string } = {},
): string | null {
  const number = toWhatsAppNumber(raw, options.country ?? 'RO');
  if (!number) return null;
  return options.message
    ? `https://wa.me/${number}?text=${encodeURIComponent(options.message)}`
    : `https://wa.me/${number}`;
}

